import { useRef, useState, useEffect } from 'react';
import { Auth } from 'Shared/auth';
import { GoogleOAuthProvider, GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import GoogleLogo from 'Shared/icons/logos/google.svg';
import { RecentlyUsedBadge } from './RecentlyUsedBadge';

export function GoogleSSOButton({
   loginHandler,
   setError,
   text,
   recentlyUsed,
}: {
   loginHandler: (response: any) => void;
   setError: (error: string) => void;
   text: string;
   recentlyUsed?: boolean;
}) {
   const wrapperRef = useRef<HTMLDivElement>(null);
   const [googleButton, setGoogleButton] = useState<HTMLElement | null>(null);
   const [showCustomButton, setShowCustomButton] = useState(true);

   useEffect(() => {
      (window as any).onGoogleLibraryLoad = () => {
         // Firefox can't find the button without this because it's in a cross-origin iframe.
         // There's a brief moment where the button isn't in an iframe and Firefox can find it.
         // requestAnimationFrame seems to get the button in this moment consistently.
         window.requestAnimationFrame(() => {
            const googleButton = wrapperRef.current?.querySelector('div[role=button]');
            if (googleButton instanceof HTMLElement) {
               setGoogleButton(googleButton);
            } else {
               // Fall back to the default button
               setShowCustomButton(false);
            }
         });
      };
      // eslint-disable-next-line react-hooks/refs
   }, [wrapperRef.current]);

   const signinCallback = (credentialResponse: CredentialResponse) => {
      if (credentialResponse.credential) {
         (Auth as any).sendLogin({
            googleOAuthData: credentialResponse,
            ajax: true,
            handler: loginHandler,
         });
      } else {
         setError(App.loginMessages.OAuth2_ACCESS_TOKEN);
      }
   };

   const errorCallback = () => {
      setError(App.loginMessages.OAuth2_ERROR);
   };

   return (
      <GoogleOAuthProvider clientId={App.googleOAuth2Clientid}>
         <div id="googleLogin" className="w-full" data-mobile-appid="googleLogin">
            {showCustomButton && (
               <button
                  className="button-outline button-lg w-full gap-2 z-1"
                  onClick={() => {
                     if (googleButton instanceof HTMLElement) {
                        googleButton.click();
                        return;
                     }
                     const newGoogleButton = wrapperRef.current?.querySelector('div[role=button]');
                     if (newGoogleButton instanceof HTMLElement) {
                        setGoogleButton(newGoogleButton);
                        newGoogleButton.click();
                     }
                  }}
               >
                  <GoogleLogo className="size-6.5" />
                  {text}
               </button>
            )}
            <div className={`z-1 ${showCustomButton ? 'hidden' : ''}`} ref={wrapperRef}>
               <GoogleLogin onSuccess={signinCallback} onError={errorCallback} locale={App.lang} />
            </div>
            {recentlyUsed && <RecentlyUsedBadge />}
         </div>
      </GoogleOAuthProvider>
   );
}
